import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  CircularProgress,
  Collapse,
  Button,
  Tooltip,
  Alert,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Casino as CasinoIcon,
  QuestionAnswer as QuestionAnswerIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  Language as LanguageIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { paaService, PAAQuestion } from '../../services/paaService';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { websiteService } from '../../services/websiteService';
import { sortWebsitesByOrder } from '../../utils/countryFlags';

interface PAAQuestionSelectorProps {
  onQuestionSelect: (question: string) => void | Promise<void>;
  selectedWebsiteId?: string;
  selectedUrlId?: string;
}

export const PAAQuestionSelector: React.FC<PAAQuestionSelectorProps> = ({
  onQuestionSelect,
  selectedWebsiteId,
  selectedUrlId,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [questions, setQuestions] = useState<PAAQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [websites, setWebsites] = useState<any[]>([]);
  const [selectedWebsite, setSelectedWebsite] = useState<string>('');
  const [clickedQuestionId, setClickedQuestionId] = useState<number | null>(null);

  // Get current website from Redux if not provided
  const reduxSelectedWebsite = useSelector((state: RootState) => state.website.selectedWebsite);
  const websiteId = selectedWebsite || selectedWebsiteId || (reduxSelectedWebsite?.id ? String(reduxSelectedWebsite.id) : undefined);

  // Load websites on mount
  useEffect(() => {
    loadWebsites();
  }, []);

  // Load questions when component mounts or filters change
  useEffect(() => {
    if (isExpanded && websiteId) {
      loadQuestions();
    }
  }, [isExpanded, websiteId, selectedUrlId, searchQuery]);

  const loadWebsites = async () => {
    try {
      const { websites: data, error } = await websiteService.getWebsites();
      if (error) {
        console.error('Error loading websites:', error);
        return;
      }
      
      const sortedWebsites = sortWebsitesByOrder(data);
      setWebsites(sortedWebsites);
      
      // Set default website if available
      if (sortedWebsites.length > 0 && !selectedWebsite) {
        setSelectedWebsite(sortedWebsites[0].website_id);
      }
    } catch (err) {
      console.error('Error loading websites:', err);
    }
  };

  const loadQuestions = async () => {
    if (!websiteId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await paaService.getQuestions({
        website_id: websiteId as string,
        url_id: selectedUrlId,
        search: searchQuery,
        limit: 30,
      });
      
      setQuestions(response.questions);
    } catch (err: any) {
      console.error('Error loading PAA questions:', err);
      setError(err.message || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  };


  const handleSurpriseMe = async () => {
    try {
      const surprise = await paaService.getSurpriseQuestion(websiteId);
      if (surprise) {
        onQuestionSelect(surprise.question);
        setIsExpanded(false);
      }
    } catch (err) {
      console.error('Error getting surprise question:', err);
    }
  };

  const handleQuestionClick = async (questionId: number, question: string) => {
    setClickedQuestionId(questionId);
    await onQuestionSelect(question);
    // Keep the question highlighted briefly before collapsing
    setTimeout(() => {
      setIsExpanded(false);
      setClickedQuestionId(null);
    }, 300);
  };

  const filteredQuestions = questions.filter(q =>
    q.question.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Paper sx={{ mb: 3, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          bgcolor: isExpanded ? 'rgba(123, 97, 255, 0.08)' : '#fafafa',
          borderBottom: isExpanded ? '1px solid rgba(123, 97, 255, 0.2)' : 'none',
          transition: 'all 0.3s',
          '&:hover': {
            bgcolor: isExpanded ? 'rgba(123, 97, 255, 0.12)' : '#f5f5f5',
          },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ 
            bgcolor: '#7b61ff',
            borderRadius: '8px',
            p: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <QuestionAnswerIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#333' }}>
            People Also Ask Questions
          </Typography>
          {questions.length > 0 && (
            <Chip 
              label={questions.length} 
              size="small" 
              sx={{ 
                bgcolor: 'rgba(123, 97, 255, 0.15)',
                color: '#7b61ff',
                fontWeight: 600,
                height: 24
              }} 
            />
          )}
        </Box>
        <IconButton size="small" sx={{ color: '#7b61ff' }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Expanded Content */}
      <Collapse in={isExpanded}>
        <Box sx={{ p: 2, pt: 0 }}>
          {/* Website Selector and Actions */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2, 
            mb: 3, 
            alignItems: { xs: 'stretch', sm: 'center' },
            pt: 2
          }}>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id="website-select-label">Select Website</InputLabel>
              <Select
                labelId="website-select-label"
                value={selectedWebsite}
                label="Select Website"
                onChange={(e: SelectChangeEvent) => setSelectedWebsite(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <LanguageIcon sx={{ fontSize: 20, color: '#7b61ff' }} />
                  </InputAdornment>
                }
              >
                {websites.map((website) => {
                  const flagEmojis: { [key: string]: string } = {
                    'UK': '🇬🇧', 'FI': '🇫🇮', 'FR': '🇫🇷', 'JP': '🇯🇵', 'CA': '🇨🇦', 
                    'DE': '🇩🇪', 'ES': '🇪🇸', 'IT': '🇮🇹', 'NL': '🇳🇱', 'AU': '🇦🇺',
                    'BR': '🇧🇷', 'CN': '🇨🇳', 'DK': '🇩🇰', 'KR': '🇰🇷', 'NO': '🇳🇴',
                    'RU': '🇷🇺', 'SE': '🇸🇪', 'COM': '🌐'
                  };
                  
                  const cleanUrl = website.base_url
                    ?.replace(/^https?:\/\//, '')
                    ?.replace(/^www\./, '') || website.website_id;
                  
                  return (
                    <MenuItem key={website.website_id} value={website.website_id}>
                      {flagEmojis[website.country] || '🌐'} {cleanUrl}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CasinoIcon />}
                onClick={handleSurpriseMe}
                sx={{ 
                  borderColor: '#7b61ff',
                  color: '#7b61ff',
                  '&:hover': {
                    borderColor: '#6250e6',
                    bgcolor: 'rgba(123, 97, 255, 0.04)'
                  }
                }}
              >
                Surprise Me
              </Button>
              <IconButton
                onClick={loadQuestions}
                sx={{ 
                  color: '#7b61ff',
                  border: '1px solid rgba(123, 97, 255, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(123, 97, 255, 0.04)',
                    borderColor: '#7b61ff'
                  }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Search */}
          <Box sx={{ mb: 3 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#7b61ff',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#7b61ff',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

              {/* Questions List */}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#7b61ff' }} />
                </Box>
              ) : error ? (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 2,
                    '& .MuiAlert-icon': {
                      color: '#d32f2f'
                    }
                  }}
                >
                  {error}
                </Alert>
              ) : filteredQuestions.length === 0 ? (
                <Alert 
                  severity="info" 
                  sx={{ 
                    bgcolor: 'rgba(123, 97, 255, 0.08)',
                    color: '#5142cc',
                    '& .MuiAlert-icon': {
                      color: '#7b61ff'
                    }
                  }}
                >
                  {searchQuery
                    ? 'No questions match your search'
                    : 'No questions found. They will appear as you analyze SERPs.'}
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {filteredQuestions.map((question) => (
                    <Paper
                      key={question.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        cursor: clickedQuestionId === question.id ? 'wait' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        transition: 'all 0.2s',
                        bgcolor: clickedQuestionId === question.id ? 'rgba(123, 97, 255, 0.08)' : 'transparent',
                        borderColor: clickedQuestionId === question.id ? '#7b61ff' : '#e0e0e0',
                        transform: clickedQuestionId === question.id ? 'translateX(4px)' : 'none',
                        boxShadow: clickedQuestionId === question.id ? '0 2px 8px rgba(123, 97, 255, 0.15)' : 'none',
                        '&:hover': {
                          bgcolor: clickedQuestionId === question.id ? 'rgba(123, 97, 255, 0.08)' : 'rgba(123, 97, 255, 0.04)',
                          borderColor: '#7b61ff',
                          transform: 'translateX(4px)',
                          boxShadow: '0 2px 8px rgba(123, 97, 255, 0.15)',
                        },
                      }}
                      onClick={() => clickedQuestionId !== question.id && handleQuestionClick(question.id, question.question)}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#333', mb: 0.5 }}>
                          {question.question}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {question.keyword && (
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              From: "{question.keyword}"
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: '#7b61ff', fontWeight: 500 }}>
                            • Click to get AI answers
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton size="small" sx={{ color: '#7b61ff' }}>
                        {clickedQuestionId === question.id ? (
                          <CircularProgress size={18} sx={{ color: '#7b61ff' }} />
                        ) : (
                          <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Paper>
                  ))}
                </Box>
              )}
        </Box>
      </Collapse>
    </Paper>
  );
};